import { useCallback, useEffect, useState } from 'react';

const leaveBrowserFullscreen = () => {
  if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
};

/**
 * A view taken to the whole screen.
 *
 * The caller pins the view over the page while `active` (fixed, inset 0); the
 * browser is put in full screen where it can be — the whole document, not
 * the view, because menus and dialogs opened from the view render at the end
 * of the body and would not show over a full-screen element. An iPhone has
 * no full screen for a page, and gets the pinned view alone.
 *
 * Escape leaves, as does the browser's own way out.
 */
export function useFullscreen() {
  const [active, setActive] = useState(false);

  const enter = useCallback(() => {
    setActive(true);
    if (document.fullscreenEnabled && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  const exit = useCallback(() => {
    setActive(false);
    leaveBrowserFullscreen();
  }, []);

  useEffect(() => {
    if (!active) return;
    // Leaving the browser's full screen (its Escape, its button) leaves ours
    const onChange = () => {
      if (!document.fullscreenElement) setActive(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Escape closes an open menu or dialog first
      if (document.querySelector('[role="dialog"], [role="menu"]')) return;
      setActive(false);
      leaveBrowserFullscreen();
    };
    // The page underneath does not scroll while the view covers it
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('keydown', onKey);
      // A view that goes away takes the browser out of full screen with it
      leaveBrowserFullscreen();
    };
  }, [active]);

  return { active, enter, exit, toggle: active ? exit : enter };
}
